import _ from "lodash"
import axios from "axios"
import { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react"
import { BrowserRouter as Router, useNavigate } from "react-router-dom"
import { setContext } from "@apollo/client/link/context"
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  useQuery,
  useLazyQuery,
} from "@apollo/client"

// Server URL
import serverUrl from "../config/serverUrl"

// Queries
import * as QUERIES from "./queries"

// Context
import { UserContext } from "../context/userContext"
import { FirebaseContext } from "../firebase"
import { UserActivityContext } from "../context/userActivityContext"

// Comps
import RoutesBuilder from "../components/Routes"

// Loaders
import LoaderFullPage, {
  LoaderProgressBarSideWide,
} from "../components/Loaders"

// Pages
import Login from "../pages/Login"

import {
  getSessStorage,
  setSessStorage,
  clearSessStorage,
} from "../helpers/sessionStorage"

// Cache Component
import WithClearCache from "../components/WithClearCache"

//Helper
import updateAppUser from "../helpers/appUser"

// Cloud Functions
import { useActivityTracker } from "../hooks/activityTracker/useActivityTracker"
import UserActivityWarning from "../components/UserActivityWarning"
import useTokenManager, {
  getUserToken,
} from "../hooks/tokenManager/useTokenManager"
import { callCloudFunction } from "../helpers/cloudFunctionRequests"

function HandleIsAuth(props) {
  const { session, token, authUser, handleMainTokenRefresh } = props
  const navigate = useNavigate()

  const [globalLoading, setGlobalLoading] = useState(false)

  //Call ctx
  const [audioToken, setAudioToken] = useState(null)
  const [callStatus, setCallStatus] = useState("No call in progress.")
  const [isMuted, setIsMuted] = useState(false)
  const [isOnCall, setIsOnCall] = useState(false)
  const [startCallEvent, setStartCallEvent] = useState(false)
  const [hangupEvent, setHangupEvent] = useState(false)
  const [muteOnOffEvent, setMuteOnOffEvent] = useState(false)
  const [isDeviceReady, setIsDeviceReady] = useState(false)
  const [callError, setCallError] = useState("")
  const [showCarrierBlockCallError, setShowCarrierBlockCallError] =
    useState(false)

  const defaultFromSession = {
    current_organization_id: session.user.organization_id,
    current_participant: session.participant,
    current_participant_id:
      getSessStorage("participant_id") !== ""
        ? getSessStorage("participant_id")
        : null,
    current_staff_membership_id:
      getSessStorage("current_staff_membership_id") !== ""
        ? getSessStorage("current_staff_membership_id")
        : null,
  }

  const [current_participant_id, setCurrentPid] = useState(
    defaultFromSession.current_participant_id,
  )
  const [current_organization_id, setCurrentOrganizationId] = useState(
    defaultFromSession.current_organization_id,
  )
  const [participantMembership, setParticipantMembership] = useState(
    defaultFromSession.current_participant,
  )

  const [current_staff_membership_id, setCurrentStaffMembershipId] = useState(
    defaultFromSession.current_staff_membership_id,
  )

  const [current_staff, setCurrentStaff] = useState(session.user) // TODO: Check defaults
  const [currentActiveReferral, setCurrentActiveReferral] = useState(null)

  //Notifications settings
  const [showNotificationsSettings, setShowNotificationsSettings] =
    useState(false)

  //Housing settings
  const [housing, setHousing] = useState({ showHousing: false })

  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const [toggleAppointment, setToggleAppointment] = useState({
    toggle: false,
    page: "default",
  })

  //Manages token lifetimes and refreshing
  const {
    initialTokenFetchDone,
    getMainToken,
    getNotificationToken,
    getTaskToken,
    getDocumentSignToken,
    getAppointmentsToken,
    getHousingToken,
    getTelerecoveryToken,
    getEmploymentToken,
    getAssessmentsToken,
    getGpraToken,
    getCoordinationBoardsToken,
    getKioskToken,
    getGooglePlacesKey,
    getMapBoxKey,
  } = useTokenManager(token, authUser, handleMainTokenRefresh)

  //Tracks user activity and handles automatic logout after inactivity.
  const {
    state,
    countdown,
    getLogoutAfter,
    getWarningDuration,
    getLastActive,
    resetTimer,
    logout,
    refreshSettings,
  } = useActivityTracker({
    doMonitor: !!session?.user?.staff_id, // Only monitor activity if there is a logged in user
    onWarning: () => {},
  })

  useEffect(() => {
    const userRole = session?.user?.staff_role
    const organizationType = session?.user?.organization_type
    if (userRole >= 9 && organizationType === "PARTNER") {
      navigate("/referrals/list")
    }
  }, [])

  async function setToggleAppointments(val) {
    setToggleAppointment(val)
  }

  async function refreshData() {
    setRefreshTrigger(true)
    setRefreshTrigger(false)
  }

  // IncomingCall
  const [incomingCall_QueueId, setIncomingCallQueueId] = useState(null)
  const [incomingCall_EventId, setIncomingCallEventId] = useState(null)
  const [incomingCall_PickedUp, setIncomingCallPickedUp] = useState(null)

  // Global Messages
  const [gmParticipantsConvList, setGmParticipantsConvList] = useState([])

  // System notifications
  const [notification_refresh, setNotificationRefresh] = useState(undefined)
  const [notification_Information, setNotificationInformation] =
    useState(undefined)
  const [notification_error, setNotificationError] = useState(undefined)
  const [notification_action, setNotificationAction] = useState(undefined)
  const [notification_show_state, setNotificationShowState] = useState("")

  // System tasks
  const [notification_closed, setNotificationsClosed] = useState(undefined)

  // Document Signing
  const [triggerDocumentRequestShow, setTriggerDocumentRequestShow] =
    useState(undefined)
  const [documentsQuickFilter, setDocumentsQuickFilter] = useState(undefined)

  function showDocumentRequests(quickFilterOption) {
    setDocumentsQuickFilter(quickFilterOption)
    setTriggerDocumentRequestShow(!triggerDocumentRequestShow)
  }

  const [canFetch, setCanFetch] = useState(false)

  useEffect(() => {
    updateAppUser({
      organization_id: defaultFromSession.current_organization_id ?? null,
      participant_id: defaultFromSession.current_participant_id ?? null,
      enterprise_id: current_staff?.enterprise_id ?? null,
    })
  }, [defaultFromSession])

  useEffect(() => {
    if (
      !_.isEmpty(defaultFromSession?.current_participant_id) &&
      !_.isEmpty(defaultFromSession?.current_organization_id)
    ) {
      setCanFetch(true)
    } else {
      setCanFetch(false)
    }
  }, [defaultFromSession])

  // Trigger if Participant is in SessionStorage
  const { loading: fetchingParticipant } = useQuery(QUERIES.GET_PARTICIPANT, {
    skip:
      !canFetch ||
      !defaultFromSession?.current_participant_id ||
      defaultFromSession.current_participant_id === "" ||
      !defaultFromSession?.current_organization_id ||
      defaultFromSession.current_organization_id === "",
    variables: {
      participant_id: defaultFromSession.current_participant_id,
      organization_id: defaultFromSession.current_organization_id,
    },
    onCompleted: ({ participant }) => {
      setParticipantMembership(participant)
    },
    onError: (error) => {
      console.error(
        "useQuery: Error when fetching Participant from Session Storage.",
        error,
      )
    },
  })

  // TODO: No need fetching User with new Session...
  const [getCurrentStaff] = useLazyQuery(QUERIES.GET_USER, {
    onCompleted: ({ user }) => {
      setCurrentStaff(user)
    },
  })

  const [getCurrentParticipant] = useLazyQuery(QUERIES.GET_PARTICIPANT, {
    fetchPolicy: "no-cache",
    onCompleted: ({ participant }) => setParticipantMembership(participant),
    onError: ({ error }) =>
      console.log("useLazyQuery: Error when getting Participant:", error),
  })

  const [getParticipantByPhoneNumber] = useLazyQuery(
    QUERIES.GET_PARTICIPANT_BY_PHONE,
    {
      fetchPolicy: "network-only",
    },
  )

  const [getCurrentParticipantByAliasID] = useLazyQuery(
    QUERIES.GET_PARTICIPANT_BY_ALIAS_ID,
    {
      fetchPolicy: "network-only",
    },
  )

  async function setParticipantByPhone(participantPhoneNumber) {
    await getParticipantByPhoneNumber({
      variables: {
        phone: participantPhoneNumber,
      },
      fetchPolicy: "network-only",
      onCompleted: async ({ participantByNumber }) => {
        if (participantByNumber) {
          await changeParticipant(participantByNumber.participant_id)
        }
      },
      onError: ({ error }) => {
        console.log(error)
      },
    })
  }

  async function setParticipantByAliasID(participant_alias) {
    await getCurrentParticipantByAliasID({
      variables: {
        participant_alias: participant_alias,
        staff_id: current_staff.staff_id,
        organization_id: current_staff.organization_id,
      },
      fetchPolicy: "network-only",
      onCompleted: async ({ participantAdvancedSearch }) => {
        const participant_id =
          participantAdvancedSearch[0].participant_id || null
        if (participant_id) {
          await changeParticipant(participant_id)
        }
      },
      onError: (error) => {
        console.log(error)
      },
    })
  }

  // TODO: check if refactor is possible, look site-wide
  async function setStaff(staff_id, organization_id) {
    getCurrentStaff({
      variables: { staff_id, organization_id },
    })
    setCurrentOrganizationId(organization_id)
    clearSessStorage("current_organization_id")
    setSessStorage("current_organization_id", organization_id)
    return staff_id
  }

  // TODO: Legacy, should use changeParticipant instead
  const setParticipant = async (participant_id) => {
    const resetParticipant =
      participant_id === null || participant_id === undefined
    if (resetParticipant) {
      // null clears the current participant
      return await changeParticipant(null)
    }
    await changeParticipant(participant_id)
  }

  const clearParticipant = async () => await changeParticipant(null)

  async function changeParticipant(participant_id) {
    if (participant_id === null) {
      setCurrentPid(null)
      clearSessStorage("participant_id")
      return setParticipantMembership({})
    }
    setGlobalLoading(true)
    setCurrentPid(participant_id)
    getCurrentParticipant({
      variables: {
        participant_id,
        organization_id: session.user.organization_id,
      },
      onCompleted: ({ participant }) => {
        setSessStorage("participant_id", participant_id)
        setParticipantMembership(participant)
        return setGlobalLoading(false)
      },
      onError: ({ error }) => {
        console.log("Error while changing participant", error)
        clearSessStorage("participant_id")
        return setGlobalLoading(false)
      },
    })
    setTimeout(() => {
      setGlobalLoading(false)
    }, 1000)
  }

  async function changeEnterprise(enterprise_id) {
    setGlobalLoading(true)
    changeParticipant(null)
    const params = {
      token: getMainToken(),
      enterprise_id,
      sessionId: session.user.staff_id,
    }
    try {
      const responseEnterprise= await callCloudFunction("setSessionCurrentEnterprise", params, getMainToken())


      const organization_id = responseEnterprise.data.data.organization_id
      setCurrentOrganizationId(organization_id)
      return setGlobalLoading(false)
    } catch (error) {
      console.log("Error while changing enterprise", error)
      return setGlobalLoading(false)
    }
  }

  async function changeOrganization(organization_id) {
    setGlobalLoading(true)
    changeParticipant(null)

    const params = {
      token: getMainToken(),
      organization_id,
      sessionId: session.user.staff_id,
    }
    
    try {
      await callCloudFunction("setSessionCurrentOrg", params, getMainToken())

      setCurrentOrganizationId(organization_id)
      return setGlobalLoading(false)
    } catch (error) {
      console.log("Error while changing organization", error)
      return setGlobalLoading(false)
    }
  }

  function changeCurrentStaffMembershipId(staff_membership_id) {
    if (staff_membership_id === null) {
      setCurrentStaffMembershipId(null)
      clearSessStorage("current_staff_membership_id")
      return setParticipantMembership({})
    }
    setSessStorage("current_staff_membership_id", staff_membership_id)
    setCurrentStaffMembershipId(staff_membership_id)
  }

  // Check on current_participant_id before spreading participantMembership.
  const participantData = current_participant_id ? participantMembership : {}

  const ctxValue = {
    ...session.user, // TODO: Should be rm and use sessions instead
    organization_name: _.unescape(session.user.organization_name),
    participant_id: current_participant_id || "",
    ...participantData,
    globalLoading,
    changeEnterprise,
    changeOrganization,
    changeParticipant,
    changeCurrentStaffMembershipId,
    clearParticipant,
    current_organization_id,
    organization_id: current_organization_id || "",
    current_participant_id,
    current_staff_membership_id,
    documentsQuickFilter,
    gmParticipantsConvList,
    incomingCall_EventId,
    incomingCall_PickedUp,
    incomingCall_QueueId,
    notification_Information,
    notification_action,
    notification_error,
    notification_refresh,
    notification_closed,
    notification_show_state,
    refreshData,
    setGlobalLoading,
    session,
    refreshTrigger,
    setGmParticipantsConvList,
    setIncomingCallEventId,
    setIncomingCallPickedUp,
    setIncomingCallQueueId,
    setNotificationAction,
    setNotificationError,
    setNotificationInformation,
    setNotificationRefresh,
    setNotificationsClosed,
    setNotificationShowState,
    setParticipant,
    setParticipantByAliasID,
    setParticipantByPhone,
    setStaff,
    setToggleAppointments,
    showDocumentRequests,
    toggleAppointment,
    triggerDocumentRequestShow,
    currentActiveReferral,
    setCurrentActiveReferral,
    showNotificationsSettings,
    setShowNotificationsSettings,
    tokens: {
      getMainToken,
      getAppointmentsToken,
      getDocumentSignToken,
      getHousingToken,
      getNotificationToken,
      getTaskToken,
      getTelerecoveryToken,
      getAssessmentsToken,
      getEmploymentToken,
      getGpraToken,
      getKioskToken,
      getCoordinationBoardsToken,
    },
    keys: {
      getGooglePlacesKey,
      getMapBoxKey,
    },
    housing: {
      context: housing,
      setHousing,
    },
    call: {
      audioToken,
      setAudioToken,
      callStatus,
      setCallStatus,
      isOnCall,
      setIsOnCall,
      isMuted,
      setIsMuted,
      startCallEvent,
      setStartCallEvent,
      hangupEvent,
      setHangupEvent,
      muteOnOffEvent,
      setMuteOnOffEvent,
      isDeviceReady,
      setIsDeviceReady,
      callError,
      setCallError,
      showCarrierBlockCallError,
      setShowCarrierBlockCallError,
    },
  }

  const userActivityContextValue = useMemo(
    () => ({
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
      refreshSettings,
    }),
    [
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
      refreshSettings,
    ],
  )

  if (fetchingParticipant || !initialTokenFetchDone) {
    return <LoaderFullPage />
  }

  return (
    <UserContext.Provider value={ctxValue}>
      <UserActivityContext.Provider value={userActivityContextValue}>
        <UserActivityWarning />
        {globalLoading && <LoaderProgressBarSideWide />}
        <RoutesBuilder />
      </UserActivityContext.Provider>
    </UserContext.Provider>
  )
}

// testin cache
const ClearCacheComponent = WithClearCache(MainApp)

function App() {
  return <ClearCacheComponent />
}

function MainApp() {
  const [fetching, setFetching] = useState(false)

  const [errSession, setErrSession] = useState("")

  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [token, setToken] = useState(null)

  const [maintenance, setMaintenance] = useState({
    maintenance_mode: false,
    title: "",
    message: "",
    updated_at: "",
  })

  //Use this for Apollo client to track the latest token without rerendering the entire App
  const latestTokenRef = useRef(null)

  //This callback will be passed to useTokenManager to update the token whenever it is refreshed, without causing a full re-render of the App component.
  const handleMainTokenRefresh = useCallback((newToken) => {
    latestTokenRef.current = newToken
  }, [])

  const firebase = useContext(FirebaseContext)

  // Suscribe to Maintenance Mode Doc.
  useEffect(() => {
    const unsubscribe = firebase.db
      .collection("global-config")
      .doc("maintenance")
      .onSnapshot(
        (docSnap) => {
          if (docSnap.exists) {
            const data = docSnap.data()

            const updated_at = data.updated_at?.toDate
              ? data.updated_at.toDate().toISOString()
              : null

            setMaintenance({
              maintenance_mode: data.maintenance_mode || false,
              title: data.title || "",
              message: data.message || "",
              updated_at,
            })
          }
        },
        (error) => {
          console.error("Error listening to maintenance mode:", error)
        },
      )

    return () => unsubscribe()
  }, [firebase.db])

  // Suscribe to Session Doc.
  useEffect(() => {
    const fetchSessionData = async (authUser) => {
      setFetching(true)

      try {
        //Load the user token by sending the Firebase ID token to verify identity and obtain an app session token.
        const newToken = await getUserToken(authUser)

        if (newToken === null) return
        const getSession = await callCloudFunction("setSession", 
          { 
            uid: authUser.uid, 
            token: newToken 
          }, 
          newToken)

        const sessionId = getSession.data.sessionId
        const sessionDocRef = firebase.db.doc(`sessions/${sessionId}`)

        sessionDocRef.onSnapshot(
          (snapshot) => {
            const userData = snapshot?.data()?.user || undefined

            if (userData === undefined) {
              console.error("No user data found for current user")
              setFetching(false)
              setUser(null)
              setSession(null)
              return
            }

            /*Google Analytics tracking*/
            window.dataLayer.push({
              event: "login",
              staff_id: snapshot?.data()?.user?.staff_id,
              staff_membership_id: "", //pending getting this value from the backend
              organization_id: snapshot?.data()?.user?.organization_id,
              enterprise_id: snapshot?.data()?.user?.enterprise_id,
              user_role: snapshot?.data()?.user?.staff_role,
            })
            /*Google Analytics tracking*/

            setSession(snapshot?.data())
            setUser(userData)
            // setFetching(false)
          },
          (error) => {
            console.error("Error fetching document:", error)
          },
        )

        latestTokenRef.current = newToken
        setToken(newToken)
      } catch (error) {
        if (error.response) {
          const { status } = error.response
          if (status === 401) {
            setErrSession(
              "Your account is not connected to any active Organization Memberships. If you need assistance, please contact support@myrecoverylink.com",
            )
          }
        }
        console.error("Error when fetching Session Data:", error)
      } finally {
        setFetching(false)
        window.recaptchaVerifier.clear()
      }
    }

    const unsubscribe = firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        window.appUser = {
          id: authUser.uid,
          email: authUser.email,
          organization_id: null,
          enterprise_id: null,
          participant_id: null,
        }
      } else {
        window.appUser = null
      }

      if (authUser?.uid) {
        fetchSessionData(authUser)
      } else {
        setUser(null)
      }
    })

    return () => unsubscribe()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase.db])

  const sendToLogin = !user || !token || !session

  const createApolloClient = () => {
    const setAuthorizationLink = setContext(() => ({
      headers: { Authorization: latestTokenRef.current },
    }))
    const httpLink = new HttpLink({ uri: `${serverUrl}/graphql` })
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: setAuthorizationLink.concat(httpLink),
    })
  }

  const apolloClient = useMemo(() => createApolloClient(), [token])

  if (fetching) return <LoaderFullPage />

  if (sendToLogin || maintenance.maintenance_mode)
    return (
      <Login
        errSession={errSession}
        setErrSession={setErrSession}
        maintenance={maintenance}
      />
    )

  return (
    <ApolloProvider client={apolloClient}>
      <Router>
        <HandleIsAuth
          staff_id={user.uid}
          session={session}
          token={token}
          authUser={user}
          handleMainTokenRefresh={handleMainTokenRefresh}
        />
      </Router>
    </ApolloProvider>
  )
}

export default App
